/**
 * =============================================================================
 * ListPageHeader — Cabeçalho compacto padrão para páginas de listagem.
 *
 * Estrutura idêntica ao header da página Cobranças (Financeiro):
 *   [título + descrição]   [slot central — ex.: MonthNavigator]   [ações]
 *
 * Padding e separador inferior aplicados internamente (`p-4 border-b`).
 *
 * @version 1.2.0
 * RELEASE: 2026-04-25 v1.0.0 - Criação do helper de header padronizado.
 * RELEASE: 2026-10-09 v1.2.0 - Slot `center` com largura fixa no desktop
 *   (`sm:w-[36rem]`): a coluna `auto` do grid colapsava para a largura
 *   intrínseca do conteúdo, deixando os campos de busca menores que o padrão
 *   da listagem de Negócios. Conteúdo continua centralizado.
 * RELEASE: 2026-04-28 v1.1.0 - Centralização real do slot `center` via grid
 *   3 colunas (1fr | auto | 1fr) no desktop. O layout legado com flex
 *   `justify-between` apenas distribuía o espaço entre os 3 itens, fazendo
 *   o slot central ficar deslocado conforme a largura do título e das ações.
 *   Agora, quando há `center`, ele é literalmente centralizado em relação
 *   ao container, sem afetar páginas que não usam esse slot.
 * =============================================================================
 */
import React from 'react';
import { cn } from '@/lib/utils';

interface ListPageHeaderProps {
  /** Título principal (ex.: "Comissões"). */
  title: string;
  /** Descrição curta abaixo do título. */
  description?: string;
  /** Slot central (ex.: <MonthNavigator />). */
  center?: React.ReactNode;
  /** Slot de ações à direita (ex.: refresh, "Novo X"). */
  actions?: React.ReactNode;
  /** Classes adicionais. */
  className?: string;
}

/** Cabeçalho fixo da página de listagem. */
export function ListPageHeader({
  title,
  description,
  center,
  actions,
  className,
}: ListPageHeaderProps) {
  // Quando há slot central, alteramos a estratégia de layout no desktop
  // para um grid 3 colunas (1fr | auto | 1fr), de modo que o item do meio
  // fique fisicamente centralizado em relação ao container — independente
  // da largura do título ou das ações.
  const hasCenter = Boolean(center);

  return (
    <div
      className={cn(
        // Mobile (<sm): empilha em duas linhas — (título+ações) e (center centralizado).
        'flex flex-col gap-3 p-4 border-b',
        // Desktop:
        // - COM center  → grid de 3 colunas, centro real.
        // - SEM center  → flex justify-between (legado) — zero impacto.
        hasCenter
          ? 'sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-4'
          : 'sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {/* Linha 1 mobile: título à esquerda + ações à direita na mesma linha.
          No desktop, o título mantém sua célula original (esquerda do grid). */}
      <div
        className={cn(
          'flex items-start justify-between gap-3',
          // No desktop volta a ser bloco simples (apenas título); ações têm
          // sua própria célula no grid/flex.
          'sm:block',
          hasCenter && 'sm:justify-self-start min-w-0',
        )}
      >
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">{title}</h1>
          {description && (
            // Subtítulo oculto em mobile para economizar espaço vertical
            <p className="hidden sm:block text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {/* Ações no mobile aparecem aqui, na mesma linha do título.
            No desktop ficam ocultas — a versão "real" de ações está abaixo
            (com posicionamento de grid correto). */}
        {actions && (
          <div className="flex flex-wrap items-center justify-end gap-2 sm:hidden">
            {actions}
          </div>
        )}
      </div>

      {/* Slot central — centralizado tanto no mobile (linha própria) quanto
          no desktop (célula central do grid).
          v1.2.0: largura fixa no desktop (`sm:w-[36rem]`) — a coluna `auto`
          do grid colapsava para a largura intrínseca do conteúdo, deixando
          os campos de busca menores que o padrão da listagem de Negócios. */}
      {hasCenter && (
        <div className="flex w-full justify-center sm:w-[36rem] sm:max-w-full sm:justify-self-center">{center}</div>
      )}

      {/* Ações à direita (desktop). No mobile ficam ocultas porque já foram
          renderizadas ao lado do título acima. */}
      {actions && (
        <div
          className={cn(
            'hidden sm:flex flex-wrap items-center gap-2',
            hasCenter && 'sm:justify-self-end',
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
